import { React, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import Lottie from 'lottie-react-web';

// Model //
import FetchModule from '../../../Model/Network/FetchModule';
// Model //

// Animation //
import LoadingAnimation from '../../../Animation/LoadingAnimation.json';
// Animation //


export default function MoveEventModal(props) {
  const { data, refresh, onClickExit } = props;

  const [divisions, setDivisions] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(-1);

  const [context, setContext] = useState(null);

  const [optionGroups, setOptionGroups] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({});

  const [isListLoading, setIsListLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('변경 중');

  const navigate = useNavigate();

  // 스크롤 정지 //
  useEffect(() => {
    document.body.style = `overflow: hidden`;
    return () => document.body.style = `overflow: auto`;
  }, [])

  // 부문 변경 모달 데이터 불러오기 ( 부문 목록 + 현재 선택 통합 1콜 ) //
  useEffect(() => {
    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.getDataOfEventVer2('sales/change/summary/' + data.merchantUid, 'GET');

        if (responseData.status === 200) {
          // 같은 경기 형태(팀전/개인전) 전 부문 ( 현재 부문 포함 ) //
          const newDivisions = responseData.data.divisions.filter((division) => division.isTeamFight === data.isTeamFight);
          const currentSelections = responseData.data.currentSelections;

          setDivisions(newDivisions);
          setContext({ selections: currentSelections });
          setIsListLoading(false);

          // 현재 부문이 옵션 대회면 기본 선택 + 현재값으로 옵션 편집 준비 //
          const current = newDivisions.find((division) => division.eventId === data.eventId);

          if (current !== undefined && current.isUseSelectOption) {
            const groups = current.optionGroups || [];

            setSelectedEventId(current.eventId);
            setOptionGroups(groups);
            setSelectedOptions(buildDefaultSelections(groups, currentSelections));
          }
        }
        else if (responseData.status === 401) {
          navigate('/login');
        }
        else {
          setIsListLoading(false);
        }
      }
    )();
  }, [data])

  // 로딩 텍스트 변경 //
  useEffect(() => {
    if (!isLoading) return;

    setTimeout(() => {
      if (loadingText === '변경 중') setLoadingText('변경 중.');
      else if (loadingText === '변경 중.') setLoadingText('변경 중..');
      else if (loadingText === '변경 중..') setLoadingText('변경 중...');
      else if (loadingText === '변경 중...') setLoadingText('변경 중');
    }, 800);
  }, [isLoading, loadingText])

  // 결제금액 단위 결정 //
  function decidePriceUnit() {
    if (data.isGlobalPayment) return 'USD';
    else return '원';
  }

  // 선택 부문의 결제금액 결정 //
  function decideDivisionPrice(division) {
    if (data.isGlobalPayment) return division.priceUsd;
    else return division.price;
  }

  // 현재 선택 내역 리스트 반환 ( context 안전 접근 ) //
  function getSelectionList() {
    if (context === null || context.selections === undefined) return [];
    else return context.selections;
  }

  // 옵션 개인(포지션) 그룹의 팀원 목록 추출 ( 중복 제거 ) //
  function getMembers(selections) {
    const members = [];
    const seen = new Set();

    for (const selection of selections) {
      if (!selection.isIndividualOption) continue;
      if (selection.memberId === undefined || selection.memberId === null) continue;
      if (seen.has(selection.memberId)) continue;

      seen.add(selection.memberId);
      members.push({ memberId: selection.memberId, memberName: selection.memberName });
    }

    return members;
  }

  // 현재 선택한 옵션(selectedOptionId)을 기본값으로 매칭 ( groupId + optionId 기준 ) //
  function buildDefaultSelections(groups, selections) {
    const result = {};
    const members = getMembers(selections);

    for (const group of groups) {
      if (group.isIndividualOption) {
        const memberMap = {};

        for (const member of members) {
          const current = selections.find((selection) => selection.groupId === group.groupId && selection.memberId === member.memberId);
          const matched = current === undefined ? undefined : group.options.find((option) => option.id === current.selectedOptionId);

          if (matched !== undefined) memberMap[member.memberId] = matched.id;
        }

        result[group.groupId] = memberMap;
      }
      else {
        const current = selections.find((selection) => !selection.isIndividualOption && selection.groupId === group.groupId);
        const matched = current === undefined ? undefined : group.options.find((option) => option.id === current.selectedOptionId);

        if (matched !== undefined) result[group.groupId] = matched.id;
      }
    }

    return result;
  }

  // 부문 선택시 ( 옵션은 division 에 포함되어 있음 ) //
  function onClickDivision(division) {
    setSelectedEventId(division.eventId);
    setSelectedOptions({});

    if (!division.isUseSelectOption) {
      setOptionGroups([]);
      return;
    }

    const groups = division.optionGroups || [];

    setOptionGroups(groups);
    setSelectedOptions(buildDefaultSelections(groups, getSelectionList()));
  }

  // 단일 옵션 그룹 칩 선택시 //
  function onClickOption(groupId, optionId) {
    const newSelectedOptions = { ...selectedOptions };

    newSelectedOptions[groupId] = optionId;
    setSelectedOptions(newSelectedOptions);
  }

  // 팀원별(포지션) 옵션 칩 선택시 //
  function onClickMemberOption(groupId, memberId, optionId) {
    const newSelectedOptions = { ...selectedOptions };
    const memberMap = { ...(newSelectedOptions[groupId] || {}) };

    memberMap[memberId] = optionId;
    newSelectedOptions[groupId] = memberMap;
    setSelectedOptions(newSelectedOptions);
  }

  // 모든 옵션 그룹이 선택 완료됐는지 검증 //
  function isAllSelected() {
    const members = getMembers(getSelectionList());

    for (const group of optionGroups) {
      if (group.isIndividualOption) {
        const memberMap = selectedOptions[group.groupId];

        for (const member of members) {
          if (memberMap === undefined || memberMap[member.memberId] === undefined) return false;
        }
      }
      else {
        if (selectedOptions[group.groupId] === undefined) return false;
      }
    }

    return true;
  }

  // 전송할 selections 배열 구성 //
  function buildSelectionsPayload() {
    const members = getMembers(getSelectionList());
    const selections = [];

    for (const group of optionGroups) {
      if (group.isIndividualOption) {
        const memberMap = selectedOptions[group.groupId] || {};

        for (const member of members) {
          selections.push({ groupId: group.groupId, optionId: memberMap[member.memberId], memberId: member.memberId });
        }
      }
      else {
        selections.push({ groupId: group.groupId, optionId: selectedOptions[group.groupId] });
      }
    }

    return selections;
  }

  // 변경하기 버튼 클릭시 //
  function onClickChange() {
    if (isLoading) return;

    if (selectedEventId === -1) {
      alert('변경할 부문을 선택해주세요');
      return;
    }

    const targetDivision = divisions.find((division) => division.eventId === selectedEventId);

    if (targetDivision === undefined) {
      alert('변경할 부문을 선택해주세요');
      return;
    }

    const isSameDivision = selectedEventId === data.eventId;
    const isOptionDivision = Boolean(targetDivision.isUseSelectOption);

    if (isOptionDivision && !isAllSelected()) {
      alert('모든 옵션을 선택해주세요');
      return;
    }

    const targetPrice = decideDivisionPrice(targetDivision);

    // 다른 부문으로 이동 + 결제금액이 다르면 경고 (돈은 정산하지 않음) //
    if (!isSameDivision && targetPrice !== data.eventPrice) {
      const unit = decidePriceUnit();
      const isConfirmed = window.confirm(
        '선택한 부문은 결제금액이 다릅니다 (현재 ' + data.eventPrice.toLocaleString() + unit + ' → 대상 ' + targetPrice.toLocaleString() + unit + ').\n'
        + '차액은 정산되지 않습니다. 차액 정산이 필요하면 삭제(전액환불) 후 재신청해 주세요.\n'
        + '부문만 변경하시겠습니까?'
      );

      if (!isConfirmed) return;
    }

    const requestData = {
      merchantUid: data.merchantUid,
      targetEventId: selectedEventId
    }

    if (isOptionDivision) requestData.selections = buildSelectionsPayload();

    setIsLoading(true);

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataOfEventVer2('sales/change/admin', 'POST', requestData);

        if (responseData.status === 200) {
          setIsLoading(false);
          alert(isSameDivision ? '옵션을 변경했습니다' : '부문을 변경했습니다');
          refresh();
          onClickExit();
        }
        else if (responseData.status === 401) {
          navigate('/login');
        }
        else {
          alert(responseData.error);
          setIsLoading(false);
          return;
        }
      }
    )();
  }

  // 선택옵션 그룹명에서 "선택/입력해주세요" 프롬프트 접미사 제거 //
  function cleanGroupName(name) {
    if (name === undefined || name === null) return '';

    return name.replace(/\s*(을|를)?\s*(선택|입력)\s*해?\s*주세요\.?/g, '').trim();
  }

  // 부문 목록 렌더 //
  function renderDivisions() {
    if (isListLoading) {
      return (
        <ListEmptyWrapper>
          <Text fontFamily='NotoSansKR-Regular' fontSize={12} color='#777777' cursor='default'>부문 목록을 불러오는 중...</Text>
        </ListEmptyWrapper>
      );
    }

    if (divisions.length === 0) {
      return (
        <ListEmptyWrapper>
          <Text fontFamily='NotoSansKR-Regular' fontSize={12} color='#777777' cursor='default'>변경 가능한 부문이 없습니다</Text>
        </ListEmptyWrapper>
      );
    }

    return divisions.map((division, index) => {
      const isCurrent = division.eventId === data.eventId;

      return (
        <DivisionBox
          key={'MoveDivision-' + index}
          isSelected={selectedEventId === division.eventId}
          onClick={() => onClickDivision(division)}>
          <DivisionTextWrapper>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color='#FFFFFF' cursor='pointer'>{division.eventTitle}</Text>
            {
              isCurrent &&
              <CurrentBadge>
                <Text fontFamily='NotoSansKR-Regular' fontSize={10} color='#6DD49E' cursor='pointer'>현재</Text>
              </CurrentBadge>
            }
            {
              division.isUseSelectOption &&
              <OptionBadge>
                <Text fontFamily='NotoSansKR-Regular' fontSize={10} color='#17171B' cursor='pointer'>옵션</Text>
              </OptionBadge>
            }
          </DivisionTextWrapper>
          <Text fontFamily='NotoSansKR-Regular' fontSize={12} color='#D0D0D0' cursor='pointer'>{decideDivisionPrice(division).toLocaleString() + decidePriceUnit()}</Text>
        </DivisionBox>
      );
    });
  }

  // 팀원별(포지션) 옵션 칩 렌더 //
  function renderMemberSelectors(group) {
    return getMembers(getSelectionList()).map((member, index) => (
      <MemberSelectRow key={'MemberSel-' + group.groupId + '-' + index}>
        <MemberNameWrapper>
          <Text fontFamily='NotoSansKR-Regular' fontSize={12} color='#D0D0D0' cursor='default'>{member.memberName}</Text>
        </MemberNameWrapper>
        <ChipWrapper>
          {
            group.options.map((option, oIndex) => {
              const isSelected = selectedOptions[group.groupId] !== undefined && selectedOptions[group.groupId][member.memberId] === option.id;

              return (
                <OptionChip
                  key={'MOpt-' + group.groupId + '-' + index + '-' + oIndex}
                  isSelected={isSelected}
                  onClick={() => onClickMemberOption(group.groupId, member.memberId, option.id)}>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={13} color={isSelected ? '#17171B' : '#B0B0B0'} cursor='pointer'>{option.name}</Text>
                </OptionChip>
              );
            })
          }
        </ChipWrapper>
      </MemberSelectRow>
    ));
  }

  // 선택 부문 옵션 그룹 렌더 //
  function renderOptionGroups() {
    const targetDivision = divisions.find((division) => division.eventId === selectedEventId);

    if (targetDivision === undefined || !targetDivision.isUseSelectOption) return null;

    if (optionGroups.length === 0) {
      return (
        <OptionGroupsWrapper>
          <Text fontFamily='NotoSansKR-Regular' fontSize={12} color='#777777' cursor='default'>선택할 옵션이 없습니다</Text>
        </OptionGroupsWrapper>
      );
    }

    return (
      <OptionGroupsWrapper>
        {
          optionGroups.map((group, index) => (
            <GroupBox key={'OptionGroup-' + index}>
              <GroupLabelWrapper>
                <Text fontFamily='NotoSansKR-Medium' fontSize={12} color='#6DD49E' cursor='default'>{cleanGroupName(group.groupName)}</Text>
              </GroupLabelWrapper>
              {
                group.isIndividualOption ?
                  renderMemberSelectors(group)
                  :
                  <ChipWrapper>
                    {
                      group.options.map((option, oIndex) => (
                        <OptionChip
                          key={'Opt-' + index + '-' + oIndex}
                          isSelected={selectedOptions[group.groupId] === option.id}
                          onClick={() => onClickOption(group.groupId, option.id)}>
                          <Text fontFamily='NotoSansKR-Medium' fontSize={13} color={selectedOptions[group.groupId] === option.id ? '#17171B' : '#B0B0B0'} cursor='pointer'>{option.name}</Text>
                        </OptionChip>
                      ))
                    }
                  </ChipWrapper>
              }
            </GroupBox>
          ))
        }
      </OptionGroupsWrapper>
    );
  }

  return (
    <TopLevelWrapper>
      {
        isLoading ?
          <ViewWrapper>
            <LottieWrapper>
              <Lottie options={{ animationData: LoadingAnimation }} />
            </LottieWrapper>
            <LoadingTextWrapper>
              <Text fontFamily='NotoSansKR-Bold' fontSize={16} color='#FFFFFF' cursor='default'>{loadingText}</Text>
            </LoadingTextWrapper>
          </ViewWrapper>
          :
          <ModalTotalWrapper>
            <ModalView>
              <TitleWrapper>
                <Text fontFamily='NotoSansKR-Bold' fontSize={16} color='#FFFFFF' cursor='default'>부문 · 옵션 변경</Text>
              </TitleWrapper>
              <ExplanationWrapper>
                <Text fontFamily='NotoSansKR-Regular' fontSize={12} color='#FFFFFF' cursor='default'>결제(카드·금액·상태)는 그대로 유지됩니다.</Text>
                <Text fontFamily='NotoSansKR-Regular' fontSize={12} color='#FFFFFF' cursor='default'>부문을 옮기거나, 현재 부문의 옵션을 변경할 수 있어요.</Text>
              </ExplanationWrapper>
              <DivisionListWrapper>
                {renderDivisions()}
              </DivisionListWrapper>
              {renderOptionGroups()}
              <ButtonWrapper>
                <ExitButton onClick={onClickExit}>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={14} color='#FFFFFF' cursor='pointer'>취소</Text>
                </ExitButton>
                <ChangeButton onClick={onClickChange}>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={14} color='#FFFFFF' cursor='pointer'>변경하기</Text>
                </ChangeButton>
              </ButtonWrapper>
            </ModalView>
          </ModalTotalWrapper>
      }
    </TopLevelWrapper>
  )
}

const startAnimation = keyframes`
	0% { opacity: 0; }
	50% { opacity: 0.5; }
	100% { opacity: 1; }
`;

const TopLevelWrapper = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  z-index: 1000;

  width: 100vw;
  height: 100vh;

  overflow-y: scroll;

  background-color: rgba(0, 0, 0, 0.2);

  &::-webkit-scrollbar {
    width: 2px;
    height: 0px;
  }

  &::-webkit-scrollbar-thumb {
    border: 2px solid transparent;
    background-color: #FFFFFF;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-track {
    border-radius: 200px;
    opacity: 0;
  }

	animation-name: ${startAnimation};
	animation-duration: 0.5s;
	animation-timing-function: ease-out;
`;

const ModalTotalWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;
`;

const ModalView = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  padding: 20px 15px 10px 15px;

  width: 436px;

  border-radius: 10px;
  box-shadow: 0px 40px 50px rgba(0, 0, 0, 0.1);
  background-color: #2F2F32;

  overflow: hidden;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;
`;

const ExplanationWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  margin-top: 20px;

  width: 100%;

  gap: 4px;
`;

const DivisionListWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  margin-top: 14px;

  width: 100%;
  max-height: 200px;

  gap: 8px;

  overflow-x: hidden;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 2px;
    height: 0px;
  }

  &::-webkit-scrollbar-thumb {
    border: 2px solid transparent;
    background-color: #FFFFFF;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-track {
    border-radius: 200px;
    opacity: 0;
  }
`;

const DivisionBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  box-sizing: border-box;

  padding: 0px 16px;

  width: 100%;
  min-height: 44px;

  border: ${props => props.isSelected ? `1px solid #6DD49E` : `1px solid #2F2F32`};
  border-radius: 8px;
  background-color: ${props => props.isSelected ? `#3A4A40` : `#17171B`};

  :hover {
    cursor: pointer;
  }

  transition: all 0.3s ease-in-out;
`;

const DivisionTextWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 70%;

  gap: 6px;

  overflow: hidden;
`;

const CurrentBadge = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 1px 6px;

  border: 1px solid #6DD49E;
  border-radius: 4px;
`;

const OptionBadge = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 1px 6px;

  border-radius: 4px;
  background-color: #6DD49E;
`;

const ListEmptyWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 60px;
`;

const OptionGroupsWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  box-sizing: border-box;

  padding: 12px;
  margin-top: 12px;

  width: 100%;

  gap: 14px;

  border-radius: 8px;
  background-color: #17171B;
`;

const GroupBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 8px;
`;

const GroupLabelWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
`;

const MemberSelectRow = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 6px;
`;

const MemberNameWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
`;

const ChipWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

  justify-content: flex-start;
  align-items: center;

  width: 100%;

  gap: 6px;
`;

const OptionChip = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  box-sizing: border-box;

  padding: 0px 14px;

  min-height: 34px;

  border: ${props => props.isSelected ? `1px solid #6DD49E` : `1px solid #3F3F3F`};
  border-radius: 8px;
  background-color: ${props => props.isSelected ? `#6DD49E` : `#17171B`};

  :hover {
    cursor: pointer;
  }

  transition: all 0.3s ease-in-out;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  margin-top: 30px;

  width: 100%;

  gap: 10px;
`;

const ExitButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 62px;
  height: 40px;

  border-radius: 10px;
  background-color: #17171B;

  transition: all 0.3s ease-in-out;

  :hover {
    cursor: pointer;
  }
`;

const ChangeButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100px;
  height: 40px;

  border-radius: 10px;
  background-color: #6DD49E;

  transition: all 0.3s ease-in-out;

  :hover {
    cursor: pointer;
  }
`;

const ViewWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  gap: 40px;

  width: 180px;
  height: 240px;

  overflow: hidden;
`;

const LottieWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 160px;
  height: 160px;
`;

const LoadingTextWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 140px;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  :hover {
    cursor: ${props => props.cursor};
  }
`;
